import { getActiveIndicatorPalette } from '../theme/colorBlindMode';

export type IndicatorBucket = 'grey' | 'red' | 'yellow' | 'green' | 'completed' | 'notApplicable';

/** Standard (non-accessible) palette — kept as a static export for callers that
 *  explicitly need the original colors (e.g. hex matching in indicatorBucket). */
export const BUCKET_COLORS: Record<IndicatorBucket, string> = {
  grey:           '#9CA3AF',
  red:            '#D53541',
  yellow:         '#ffd13a',
  green:          '#45BB47',
  completed:      '#106DD5',
  notApplicable:  '#4B5563',
};

function normString(s: string) {
  return String(s || '').trim().toLowerCase();
}

/**
 * Attempts to convert a stored indicator value into a semantic bucket.
 * Supports:
 * - semantic strings: "red"|"yellow"|"green"|"grey"/"gray"/"n/a"
 * - numeric codes: 0/1/2/3 (or "1"/"2"/"3")
 * - hex strings that match our known palette
 *
 * Anything unknown becomes 'grey' (no data).
 */
export function indicatorBucket(value: unknown): IndicatorBucket {
  if (value == null) return 'grey';

  if (typeof value === 'string') {
    const s = normString(value);
    if (!s) return 'grey';

    if (s === 'green') return 'green';
    if (s === 'yellow' || s === 'amber') return 'yellow';
    if (s === 'red') return 'red';
    if (s === 'grey' || s === 'gray' || s === 'none') return 'grey';
    if (s === 'completed' || s === 'complete' || s === 'done' || s === 'blue') return 'completed';
    if (s === 'notapplicable' || s === 'not_applicable' || s === 'na' || s === 'n/a' || s === 'not applicable') return 'notApplicable';

    // Common app-facing labels
    if (s === 'good' || s === 'ok' || s === 'healthy' || s === 'in progress' || s === 'inprogress') return 'green';
    if (s === 'watch' || s === 'warning' || s === 'caution') return 'yellow';
    if (s === 'atrisk' || s === 'at risk' || s === 'risk' || s === 'bad' || s === 'critical') return 'red';
    if (s === 'no data' || s === 'nodata' || s === 'unknown') return 'grey';

    // Known hex palette mappings (standard palette)
    if (s.startsWith('#')) {
      if (s.includes('d53541')) return 'red';
      if (s.includes('ffd13a')) return 'yellow';
      if (s.includes('45bb47')) return 'green';
      if (s.includes('9ca3af')) return 'grey';
      if (s.includes('106dd5')) return 'completed';
      if (s.includes('4b5563')) return 'notApplicable';
    }

    const n = Number(s);
    if (!Number.isFinite(n)) return 'grey';
    value = n;
  }

  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 'grey';
  if (n === 0) return 'grey';
  if (n === 1) return 'red';
  if (n === 2) return 'yellow';
  if (n === 3) return 'green';

  // Defensive ranges
  if (n <= 0.5) return 'grey';
  if (n <= 1.5) return 'red';
  if (n <= 2.5) return 'yellow';
  return 'green';
}

/**
 * Resolve a bucket to its display color.
 * Respects the user's color-blind mode preference via localStorage.
 * Components using the React context will trigger re-renders automatically;
 * this function reads the preference directly for non-component call sites.
 */
export function bucketToColor(bucket: IndicatorBucket): string {
  const palette = getActiveIndicatorPalette();
  return palette[bucket] || palette.grey;
}
